import { Component } from '@theme/component';

const toNumber = (value, fallback) => {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : fallback;
};

const toAspectRatio = (value, fallback = 0) => {
  const parts = `${value ?? ''}`.split('/').map((part) => Number.parseFloat(part.trim()));
  if (parts.length === 2 && parts.every((part) => Number.isFinite(part)) && parts[1] !== 0) {
    return parts[0] / parts[1];
  }
  return toNumber(value, fallback);
};

const modulo = (value, divisor) => ((value % divisor) + divisor) % divisor;
const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
const RING_SETTLE_DURATION = 560;
const DRAG_START_DISTANCE = 8;
const SCROLL_GESTURE_IDLE_DURATION = 300;
const STICKY_POSITION_TOLERANCE = 3;

/**
 * A draggable spatial carousel rendered as a 3D ring or a fanned card stack.
 */
class CarouselRing extends Component {
  #abortController;
  #resizeObserver;
  #visibilityObserver;
  #cardsObserver;
  #cards = [];
  #controlledIframes = new WeakSet();
  #activeIndex = -1;
  #pendingMediaTimer;
  #rotation = 0;
  #step = 360;
  #dragStartX = 0;
  #dragStartY = 0;
  #dragStartRotation = 0;
  #dragPointerId;
  #dragAxis;
  #dragMoved = false;
  #suppressClick = false;
  #pageWrapper;
  #stickySection;
  #stickyTarget;
  #stickyActive = false;
  #stickyStart = 0;
  #stickyEnd = 0;
  #lastScrollPosition;
  #stickyScrollFrame;
  #stickyMetricsFrame;
  #lenisScrollUnsubscribe;
  #lenisVirtualScrollUnsubscribe;
  #scrollGestureActive = false;
  #scrollGestureTimer;
  #desktopScrollDelta = 0;
  #touchStartX;
  #touchStartY;
  #touchLastY;
  #touchAxis;
  #touchGestureCaptured = false;
  #touchGestureAction;
  #touchOutwardDelta = 0;
  #touchPageSyncPending = false;
  #touchPageSyncTimer;
  #scrollSyncInProgress = false;
  #scrollSyncId = 0;
  #scrollSyncTimer;
  #sideTextOverlay;
  #sideTextOverlayKey = '';
  #sideTextTimer;
  #geometry;
  #isNearViewport = false;
  #slowNonStickyMode = false;

  #getPresentation() {
    if (window.matchMedia('(max-width: 749px)').matches) {
      return this.getAttribute('presentation-mobile') || this.getAttribute('presentation') || 'circle';
    }
    return this.getAttribute('presentation-desktop') || this.getAttribute('presentation') || 'circle';
  }

  #usesSlowNonStickyMode() {
    return this.#slowNonStickyMode;
  }

  #hasFiniteBounds() {
    return this.#stickyActive || this.#usesSlowNonStickyMode() || this.#getPresentation() === 'arc';
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const scene = this.refs.scene;
    const ring = this.refs.ring;

    if (!(scene instanceof HTMLElement) || !(ring instanceof HTMLElement)) return;

    scene.addEventListener('pointerdown', this.#onPointerDown, { signal });
    scene.addEventListener('pointermove', this.#onPointerMove, { signal });
    scene.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    window.addEventListener('pointerup', this.#onPointerUp, { signal });
    window.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    this.addEventListener('click', this.#onClick, { capture: true, signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    window.addEventListener('resize', this.#onViewportResize, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', this.#onViewportResize, { passive: true, signal });
    window.addEventListener('tnc:performance-profile-change', this.#onPerformanceProfileChange, { signal });

    this.#pageWrapper = document.querySelector('.page-wrapper');
    this.#stickySection = this.closest('.section-carousel--mobile-sticky');
    this.#stickyTarget = this.closest('.carousel-ring-sticky-target');
    this.#slowNonStickyMode = Boolean(window.tncIsSlowDevice?.() && this.#stickySection);
    if (this.#slowNonStickyMode) this.#stickySection?.setAttribute('data-tnc-nonsticky-carousel', '');

    if (!window.tncIsSlowDevice?.()) {
      document.addEventListener('wheel', this.#onWheel, { capture: true, passive: false, signal });
      document.addEventListener('touchstart', this.#onTouchStart, { capture: true, passive: true, signal });
      document.addEventListener('touchmove', this.#onTouchMove, { capture: true, passive: false, signal });
      document.addEventListener('touchend', this.#onTouchEnd, { capture: true, passive: true, signal });
      document.addEventListener('touchcancel', this.#onTouchEnd, { capture: true, passive: true, signal });
      window.addEventListener('scroll', this.#onWindowScroll, { passive: true, signal });
      window.addEventListener('tnc:lenis-change', this.#bindLenisScroll, { signal });
      this.#pageWrapper?.addEventListener('scroll', this.#onWindowScroll, { passive: true, signal });
      this.#bindLenisScroll();
    }

    this.#resizeObserver = new ResizeObserver(this.#refresh);
    this.#resizeObserver.observe(scene);

    this.#cardsObserver = new MutationObserver(this.#refresh);
    this.#cardsObserver.observe(ring, { childList: true, subtree: true });

    this.#visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isNearViewport = entry?.isIntersecting ?? false;
        if (this.#isNearViewport) this.#layout();
        else this.#suspendMedia();
      },
      { rootMargin: '0px' }
    );
    this.#visibilityObserver.observe(this);

    this.setAttribute('instant', '');
    this.#refresh();
    this.#refreshStickyMetrics();
    requestAnimationFrame(() => {
      this.removeAttribute('instant');
      this.#refreshStickyMetrics();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#pendingMediaTimer);
    clearTimeout(this.#scrollGestureTimer);
    clearTimeout(this.#scrollSyncTimer);
    clearTimeout(this.#sideTextTimer);
    cancelAnimationFrame(this.#stickyScrollFrame);
    cancelAnimationFrame(this.#stickyMetricsFrame);
    clearTimeout(this.#touchPageSyncTimer);
    this.#lenisScrollUnsubscribe?.();
    this.#lenisVirtualScrollUnsubscribe?.();
    this.#cards.forEach((card) => this.#pauseCardMedia(card));
    this.#stickySection?.style.removeProperty('--carousel-mobile-sticky-extra');
    this.#stickySection?.removeAttribute('carousel-sticky-measuring');
    this.#stickySection?.removeAttribute('data-tnc-nonsticky-carousel');
    this.#stickyTarget?.removeAttribute('carousel-touch-capture');
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#cardsObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
  }

  next(event) {
    event?.preventDefault();
    this.#selectRelative(1);
  }

  previous(event) {
    event?.preventDefault();
    this.#selectRelative(-1);
  }

  /**
   * Rotates a particular card to the front using the shortest route.
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  select(index, options = {}) {
    const count = this.#cards.length;
    if (!count) return;

    this.#suspendMedia();

    if (this.#hasFiniteBounds()) {
      this.#rotation = clamp(index, 0, count - 1) * this.#step;
    } else {
      const currentPosition = Math.round(this.#rotation / this.#step);
      const currentIndex = modulo(currentPosition, count);
      let difference = modulo(index - currentIndex, count);

      if (difference > count / 2) difference -= count;

      this.#rotation = (currentPosition + difference) * this.#step;
    }

    this.toggleAttribute('instant', Boolean(options.instant));
    this.#layout();
    this.#syncWindowToRotation();

    if (options.instant) requestAnimationFrame(() => this.removeAttribute('instant'));
  }

  #selectRelative(direction, options = {}) {
    if (this.#cards.length < 2) return;
    this.#suspendMedia();
    let position = Math.round(this.#rotation / this.#step) + direction;
    if (this.#hasFiniteBounds()) position = clamp(position, 0, this.#cards.length - 1);
    this.#rotation = position * this.#step;
    this.#layout();
    if (options.syncPage !== false) this.#syncWindowToRotation();
  }

  #refresh = () => {
    const ring = this.refs.ring;
    if (!(ring instanceof HTMLElement)) return;

    const nextCards = Array.from(ring.querySelectorAll(':scope > [data-carousel-card]')).filter(
      (card) => card instanceof HTMLElement
    );
    const cardsChanged =
      nextCards.length !== this.#cards.length || nextCards.some((card, index) => card !== this.#cards[index]);

    if (cardsChanged) {
      this.#suspendMedia();
      this.#sideTextOverlayKey = '';
    }

    this.#cards = nextCards;
    this.#step = this.#cards.length ? 360 / this.#cards.length : 360;
    this.#geometry = undefined;

    this.#cards.forEach((card, index) => {
      card.dataset.ringIndex = `${index}`;
    });

    this.#layout();
    this.#scheduleStickyMetrics();
  };

  #onViewportResize = () => {
    this.#geometry = undefined;
    this.#layout();
    this.#scheduleStickyMetrics();
  };

  #onPerformanceProfileChange = () => {
    if (window.tncIsSlowDevice?.() && this.#stickySection) {
      this.#slowNonStickyMode = true;
      this.#stickySection.setAttribute('data-tnc-nonsticky-carousel', '');
    }
    this.#geometry = undefined;
    this.#scheduleStickyMetrics();
    this.#layout();
  };

  #layout = () => {
    const scene = this.refs.scene;
    if (!(scene instanceof HTMLElement) || !this.#cards.length) return;

    const geometry = this.#geometry ?? this.#measureGeometry(scene);
    if (!geometry) return;

    const {
      cardWidth,
      cardHeight,
      cardWidths,
      cardHeights,
      horizontalRadius,
      verticalRadius,
      depthRadius,
      stackSpreadDistance,
      hasSideCardText,
    } = geometry;
    const activeIndex = modulo(Math.round(this.#rotation / this.#step), this.#cards.length);
    const stackProgress = this.#rotation / this.#step;
    const presentation = this.#getPresentation();
    const isStack = presentation === 'stack';
    const isArc = presentation === 'arc';
    const ringRotationDirection = this.getAttribute('rotation-direction') === 'right' ? -1 : 1;
    const stackFrontOffset = hasSideCardText ? 0 : cardWidth * 0.14;
    const getStackPosition = (stackSlot) => ({
      x: stackFrontOffset - stackSlot * stackSpreadDistance,
      y: stackSlot * 2.5,
      turn: 6 - stackSlot * 2.4,
      scale: Math.max(0.82, 1 - stackSlot * 0.022),
      opacity: Math.max(0.58, 1 - stackSlot * 0.055),
      zIndex: Math.round((this.#cards.length - stackSlot) * 10),
    });

    this.#cards.forEach((card, index) => {
      const renderedCardWidth = cardWidths[index] ?? cardWidth;
      const renderedCardHeight = cardHeights[index] ?? cardHeight;
      let x;
      let y;
      let transformSuffix;
      let opacity;
      let zIndex;

      if (isStack) {
        const count = this.#cards.length;
        const slot = modulo(index - stackProgress, count);

        if (slot > count - 1) {
          const outgoingProgress = count - slot;
          const sweepEnd = 0.58;

          if (outgoingProgress < sweepEnd) {
            const sweepProgress = outgoingProgress / sweepEnd;
            x = stackFrontOffset + sweepProgress * cardWidth * 0.9;
            y = -sweepProgress * cardHeight * 0.08;
            const turn = 6 + sweepProgress * 18;
            const scale = 1 - sweepProgress * 0.04;
            opacity = 1 - sweepProgress;
            zIndex = count * 10 + 10;
            transformSuffix = `rotateZ(${turn}deg) scale(${scale})`;
          } else {
            const backPosition = getStackPosition(count - 1);
            const revealProgress = (outgoingProgress - sweepEnd) / (1 - sweepEnd);
            x = backPosition.x;
            y = backPosition.y;
            opacity = backPosition.opacity * revealProgress;
            zIndex = backPosition.zIndex;
            transformSuffix = `rotateZ(${backPosition.turn}deg) scale(${backPosition.scale})`;
          }
        } else {
          const stackPosition = getStackPosition(slot);
          x = stackPosition.x;
          y = stackPosition.y;
          opacity = stackPosition.opacity;
          zIndex = stackPosition.zIndex;
          transformSuffix = `rotateZ(${stackPosition.turn}deg) scale(${stackPosition.scale})`;
        }
      } else {
        const angle = ringRotationDirection * (index * this.#step - this.#rotation);
        const radians = (angle * Math.PI) / 180;
        const sine = Math.sin(radians);
        const cosine = Math.cos(radians);
        const z = cosine * depthRadius;
        const turn = sine * 62;
        const arcSlotDistance = Math.abs(index - this.#rotation / this.#step);
        const arcOpacity = clamp((1.5 - arcSlotDistance) / 0.5, 0, 1);

        x = sine * -horizontalRadius;
        y = cosine * verticalRadius;
        opacity = isArc ? arcOpacity : 0.58 + ((cosine + 1) / 2) * 0.42;
        zIndex = Math.round(z + depthRadius + 1);
        transformSuffix = `translateZ(${z}px) rotateY(${turn}deg)`;
      }

      // Keep all per-frame movement on the compositor. Changing left/top here
      // forces layout while the sticky page itself is also being scrolled.
      const width = `${renderedCardWidth}px`;
      const height = `${renderedCardHeight}px`;
      const transform = `translate(-50%, -50%) translate3d(${x}px, ${y}px, 0) ${transformSuffix}`;
      const nextOpacity = `${opacity}`;
      const isArcHidden = isArc && opacity <= 0.01;
      const visibility = isArcHidden ? 'hidden' : 'visible';
      const nextZIndex = `${zIndex}`;

      if (card.style.width !== width) card.style.width = width;
      if (card.style.height !== height) card.style.height = height;
      if (card.style.transform !== transform) card.style.transform = transform;
      if (card.style.opacity !== nextOpacity) card.style.opacity = nextOpacity;
      if (card.style.visibility !== visibility) card.style.visibility = visibility;
      // Arc cards still occupy positions on a periodic 3D circle. WebKit can
      // composite a fully transparent card at the opposite endpoint, making
      // the first card appear after the last (or vice versa). Remove cards
      // outside the visible arc from rendering so finite bounds stay finite.
      if (card.classList.contains('carousel-ring__card--arc-hidden') !== isArcHidden) {
        card.classList.toggle('carousel-ring__card--arc-hidden', isArcHidden);
      }
      if (isArcHidden && card.getAttribute('aria-hidden') !== 'true') card.setAttribute('aria-hidden', 'true');
      else if (!isArcHidden && card.hasAttribute('aria-hidden')) card.removeAttribute('aria-hidden');
      if (card.style.zIndex !== nextZIndex) card.style.zIndex = nextZIndex;
      const isActive = index === activeIndex;
      if (card.classList.contains('card-active') !== isActive) card.classList.toggle('card-active', isActive);
      if (isActive && card.getAttribute('aria-current') !== 'true') card.setAttribute('aria-current', 'true');
      else if (!isActive && card.hasAttribute('aria-current')) card.removeAttribute('aria-current');
    });

    this.#syncSideTextOverlay(activeIndex);

    if (!this.hasAttribute('dragging') && !this.hasAttribute('scroll-driven')) {
      const shouldWaitForSettle =
        !this.hasAttribute('instant') && !window.tncIsSlowDevice?.();
      this.#scheduleMediaSync(activeIndex, shouldWaitForSettle ? RING_SETTLE_DURATION : 0);
    }

    const previous = this.refs.previous;
    const next = this.refs.next;
    const hasFiniteBounds = this.#hasFiniteBounds();
    const previousDisabled = hasFiniteBounds && this.#rotation <= 0;
    if (previous instanceof HTMLButtonElement && previous.disabled !== previousDisabled) {
      previous.disabled = previousDisabled;
    }
    if (next instanceof HTMLButtonElement) {
      const nextDisabled = hasFiniteBounds && this.#rotation >= (this.#cards.length - 1) * this.#step;
      if (next.disabled !== nextDisabled) next.disabled = nextDisabled;
    }

    const status = this.refs.status;
    const statusText = `${activeIndex + 1} / ${this.#cards.length}`;
    if (status instanceof HTMLElement && status.textContent !== statusText) status.textContent = statusText;
  };

  #measureGeometry(scene) {
    // Hidden arc cards must participate in measurement after an orientation or
    // visual-viewport resize. They are hidden again later in the same layout.
    this.#cards.forEach((card) => card.classList.remove('carousel-ring__card--arc-hidden'));

    const styles = getComputedStyle(this);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const configuredCardWidthPixels = toNumber(
      styles.getPropertyValue(isMobile ? '--carousel-ring-mobile-card-width' : '--carousel-ring-card-width'),
      220
    );
    const configuredCardHeight = toNumber(
      styles.getPropertyValue(isMobile ? '--carousel-ring-mobile-card-height' : '--carousel-ring-card-height'),
      300
    );
    const depthPercent = toNumber(styles.getPropertyValue('--carousel-ring-depth'), 26) / 100;
    const stackSpreadPercent = toNumber(styles.getPropertyValue('--carousel-stack-spread'), 12) / 100;
    const presentation = this.#getPresentation();
    const isStack = presentation === 'stack';
    const isArc = presentation === 'arc';
    const belowTextClasses = isMobile
      ? ['card-text-mobile--below', 'card-text-mobile--below-always']
      : ['card-text-desktop--below', 'card-text-desktop--below-always'];
    const sideTextClass = isMobile ? 'card-text-mobile--sides' : 'card-text-desktop--sides';
    const parentSideTextClass = isMobile
      ? 'card-text-mobile--parent-sides'
      : 'card-text-desktop--parent-sides';
    const hasBelowCardText = this.#cards.some((card) =>
      belowTextClasses.some((className) => card.classList.contains(className))
    );
    const hasSideCardText = this.#cards.some(
      (card) => card.classList.contains(sideTextClass) || card.classList.contains(parentSideTextClass)
    );
    const reflectionCards = this.#cards.filter((card) => card.classList.contains('card--floor-reflection'));
    const reflectionLength = reflectionCards.reduce(
      (maximum, card) =>
        Math.max(maximum, toNumber(getComputedStyle(card).getPropertyValue('--card-reflection-length'), 55) / 100),
      0
    );
    const reflectionGap = reflectionCards.reduce(
      (maximum, card) =>
        Math.max(maximum, toNumber(getComputedStyle(card).getPropertyValue('--card-reflection-gap'), 8)),
      0
    );
    const sceneWidth = scene.clientWidth;
    const configuredStageWidth = toNumber(styles.getPropertyValue('--carousel-ring-mobile-width'), sceneWidth);
    const mobileStageWidthUnit = this.getAttribute('stage-width-unit-mobile') || 'px';
    const mobileStageWidthRelative = toNumber(this.getAttribute('stage-width-relative-mobile'), 100);
    const mobileGeometryWidth =
      mobileStageWidthUnit === 'percent'
        ? sceneWidth * (mobileStageWidthRelative / 100)
        : mobileStageWidthUnit === 'vw'
          ? window.innerWidth * (mobileStageWidthRelative / 100)
          : configuredStageWidth;
    const geometryWidth = isMobile ? Math.max(sceneWidth, mobileGeometryWidth) : sceneWidth;
    const deviceSuffix = isMobile ? 'mobile' : 'desktop';
    const widthUnit = this.getAttribute(`card-width-unit-${deviceSuffix}`) || 'px';
    const relativeWidth = toNumber(this.getAttribute(`card-width-relative-${deviceSuffix}`), isMobile ? 58 : 30);
    const requestedCardWidth =
      widthUnit === 'percent'
        ? geometryWidth * (relativeWidth / 100)
        : widthUnit === 'vw'
          ? window.innerWidth * (relativeWidth / 100)
          : configuredCardWidthPixels;
    const heightMode = this.getAttribute(`card-height-mode-${deviceSuffix}`) || 'auto';
    const sizeBasis = this.getAttribute(`card-size-basis-${deviceSuffix}`) || 'width';
    const getCardAspectRatio = (card) => {
      const nestedVideo = card.querySelector('deferred-media, .video-placeholder-wrapper');
      const liveVideo = nestedVideo?.querySelector('video');
      const liveVideoRatio =
        liveVideo instanceof HTMLVideoElement && liveVideo.videoHeight
          ? liveVideo.videoWidth / liveVideo.videoHeight
          : 0;
      const nestedVideoRatio =
        nestedVideo instanceof HTMLElement
          ? Math.max(
            toAspectRatio(nestedVideo.dataset.videoAspectRatio, 0),
            toAspectRatio(getComputedStyle(nestedVideo).getPropertyValue('--size-style-aspect-ratio'), 0)
          )
          : 0;
      return Math.max(liveVideoRatio || nestedVideoRatio || toAspectRatio(card.dataset.cardAspectRatio, 1), 0.05);
    };

    let cardWidths;
    let cardHeights;
    if (sizeBasis === 'height') {
      const heightSvh = toNumber(this.getAttribute(`card-height-svh-${deviceSuffix}`), 60);
      const heightProbe = document.createElement('span');
      heightProbe.style.cssText = `position:fixed;visibility:hidden;pointer-events:none;height:${heightSvh}svh;`;
      this.append(heightProbe);
      const requestedCardHeight = Math.max(1, heightProbe.getBoundingClientRect().height);
      heightProbe.remove();
      cardHeights = this.#cards.map(() => requestedCardHeight);
      cardWidths = this.#cards.map((card) => requestedCardHeight * getCardAspectRatio(card));
    } else {
      const widthDrivenCardWidth =
        widthUnit === 'px'
          ? Math.min(requestedCardWidth, geometryWidth * (isStack ? 0.78 : 0.72))
          : Math.max(1, requestedCardWidth);
      cardWidths = this.#cards.map(() => widthDrivenCardWidth);
      cardHeights = this.#cards.map((card) => {
        if (heightMode !== 'auto') {
          return widthDrivenCardWidth * (configuredCardHeight / configuredCardWidthPixels);
        }

        const nestedVideo = card.querySelector('deferred-media, .video-placeholder-wrapper');
        if (nestedVideo instanceof HTMLElement) {
          const content = card.querySelector(':scope > .card__content');
          const previousCardWidth = card.style.width;
          const previousCardHeight = card.style.height;
          const previousContentHeight = content instanceof HTMLElement ? content.style.height : '';

          card.style.width = `${widthDrivenCardWidth}px`;
          card.style.height = 'auto';
          if (content instanceof HTMLElement) content.style.height = 'auto';

          let measuredHeight = Math.max(card.scrollHeight, nestedVideo.offsetHeight);
          const nestedAspectRatio = Math.max(
            toAspectRatio(nestedVideo.dataset.videoAspectRatio, 0),
            toAspectRatio(getComputedStyle(nestedVideo).getPropertyValue('--size-style-aspect-ratio'), 0)
          );
          if (nestedAspectRatio > 0) {
            const nestedWidth = nestedVideo.offsetWidth || widthDrivenCardWidth;
            measuredHeight = Math.max(measuredHeight, nestedWidth / nestedAspectRatio);
          }

          card.style.width = previousCardWidth;
          card.style.height = previousCardHeight;
          if (content instanceof HTMLElement) content.style.height = previousContentHeight;

          if (measuredHeight > 0) return measuredHeight;
        }

        return widthDrivenCardWidth / getCardAspectRatio(card);
      });
    }
    let cardWidth = Math.max(...cardWidths);
    let cardHeight = Math.max(...cardHeights);
    let horizontalRadius = this.#cards.length > 1 ? Math.max(0, (geometryWidth - cardWidth) * 0.46) : 0;
    let verticalRadius = horizontalRadius * depthPercent;
    let depthRadius = horizontalRadius * 0.85;
    const availableStackWidth = Math.max(0, geometryWidth - cardWidth * 1.35);
    let stackSpreadDistance =
      this.#cards.length > 1
        ? Math.min(cardWidth * stackSpreadPercent, availableStackWidth / (this.#cards.length - 1))
        : 0;
    const textClearance = hasBelowCardText ? (isMobile ? 150 : 200) : 0;
    const reflectionClearance = reflectionLength ? cardHeight * reflectionLength + reflectionGap : 0;
    const naturalVisualHeight = (isStack
      ? cardHeight * 1.28 + 72
      : cardHeight * (isArc ? 1.12 : 1.25) + verticalRadius * (isArc ? 1 : 2) + 48) + reflectionClearance + 100;
    let stageHeight = naturalVisualHeight + textClearance;

    if (this.getAttribute('stage-height-mode') === 'custom') {
      const requestedHeight = toNumber(
        this.getAttribute(isMobile ? 'stage-height-mobile' : 'stage-height-desktop'),
        isMobile ? 420 : 500
      );
      const availableVisualHeight = Math.max(120, requestedHeight - textClearance);
      const geometryScale = sizeBasis === 'height' ? 1 : Math.min(1, availableVisualHeight / naturalVisualHeight);

      cardWidth *= geometryScale;
      cardHeight *= geometryScale;
      cardWidths = cardWidths.map((width) => width * geometryScale);
      cardHeights = cardHeights.map((height) => height * geometryScale);
      if (!isArc) horizontalRadius *= geometryScale;
      verticalRadius *= geometryScale;
      if (!isArc) depthRadius *= geometryScale;
      stackSpreadDistance *= geometryScale;
      stageHeight = sizeBasis === 'height' ? Math.max(requestedHeight, naturalVisualHeight + textClearance) : requestedHeight;
    }

    this.style.setProperty('--carousel-ring-stage-height', `${stageHeight}px`);
    this.style.setProperty('--carousel-ring-perspective', `${Math.max(800, sceneWidth * 1.5)}px`);
    this.style.setProperty('--carousel-ring-rendered-card-width', `${cardWidth}px`);

    this.#geometry = {
      sceneWidth,
      cardWidth,
      cardHeight,
      cardWidths,
      cardHeights,
      horizontalRadius,
      verticalRadius,
      depthRadius,
      stackSpreadDistance,
      hasSideCardText,
    };
    return this.#geometry;
  }

  #syncSideTextOverlay(activeIndex) {
    const ring = this.refs.ring;
    const activeCard = this.#cards[activeIndex];
    if (!(ring instanceof HTMLElement) || !(activeCard instanceof HTMLElement)) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const modeClass = isMobile ? 'card-text-mobile--parent-sides' : 'card-text-desktop--parent-sides';
    const source = activeCard.querySelector(':scope > [data-card-configured-texts]');
    const shouldShow = activeCard.classList.contains(modeClass) && source instanceof HTMLElement;
    const overlayKey = shouldShow ? `${isMobile ? 'mobile' : 'desktop'}-${activeIndex}` : 'hidden';

    if (overlayKey === this.#sideTextOverlayKey) return;
    this.#sideTextOverlayKey = overlayKey;
    clearTimeout(this.#sideTextTimer);

    if (!(this.#sideTextOverlay instanceof HTMLElement)) {
      this.#sideTextOverlay = document.createElement('div');
      this.#sideTextOverlay.className = 'carousel-ring__side-texts';
      ring.append(this.#sideTextOverlay);
    }

    const overlay = this.#sideTextOverlay;
    overlay.removeAttribute('visible');

    if (!shouldShow) {
      this.#sideTextTimer = setTimeout(() => overlay.replaceChildren(), 380);
      return;
    }

    const renderActiveTexts = () => {
      if (this.#sideTextOverlayKey !== overlayKey) return;

      const clonedTexts = Array.from(source.querySelectorAll(':scope > .card__configured-text')).map((text) => {
        const clone = text.cloneNode(true);
        clone.classList.add('carousel-ring__side-text');
        if (clone.classList.contains('card__configured-text--one')) {
          clone.classList.add('carousel-ring__side-text--one');
        }
        if (clone.classList.contains('card__configured-text--two')) {
          clone.classList.add('carousel-ring__side-text--two');
        }
        return clone;
      });

      overlay.replaceChildren(...clonedTexts);
      overlay.getBoundingClientRect();
      if (this.#sideTextOverlayKey === overlayKey) overlay.setAttribute('visible', '');
    };

    if (overlay.childElementCount) this.#sideTextTimer = setTimeout(renderActiveTexts, 180);
    else renderActiveTexts();
  }

  #onPointerDown = (event) => {
    if (!(event instanceof PointerEvent) || !event.isPrimary || this.#cards.length < 2) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('button, input, textarea, select, model-viewer')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#dragStartX = event.clientX;
    this.#dragStartY = event.clientY;
    this.#dragStartRotation = this.#rotation;
    this.#dragPointerId = event.pointerId;
    this.#dragAxis = event.pointerType === 'mouse' ? 'horizontal' : undefined;
    this.#dragMoved = false;
  };

  #onPointerMove = (event) => {
    if (!(event instanceof PointerEvent) || event.pointerId !== this.#dragPointerId) return;

    const distance = event.clientX - this.#dragStartX;
    const verticalDistance = event.clientY - this.#dragStartY;
    if (!this.#dragAxis) {
      if (Math.max(Math.abs(distance), Math.abs(verticalDistance)) < DRAG_START_DISTANCE) return;
      this.#dragAxis = Math.abs(distance) >= Math.abs(verticalDistance) ? 'horizontal' : 'vertical';
    }

    if (this.#dragAxis === 'vertical') {
      this.#dragPointerId = undefined;
      return;
    }

    if (!this.#dragMoved) {
      if (Math.abs(distance) < DRAG_START_DISTANCE) return;
      this.#dragMoved = true;
      this.#suspendMedia();
      const scene = this.refs.scene;
      if (scene instanceof HTMLElement) scene.setPointerCapture(event.pointerId);
      this.setAttribute('dragging', '');
    }

    const scene = this.refs.scene;
    const sceneWidth = this.#geometry?.sceneWidth ?? (scene instanceof HTMLElement ? scene.clientWidth : 1);
    const degreesPerPixel = Math.max(0.18, this.#step / Math.max(sceneWidth * 0.42, 1));
    const presentation = this.#getPresentation();
    const dragDirection =
      presentation === 'stack' || this.getAttribute('rotation-direction') !== 'right' ? 1 : -1;
    this.#rotation = this.#dragStartRotation + distance * degreesPerPixel * dragDirection;
    if (this.#hasFiniteBounds()) {
      this.#rotation = clamp(this.#rotation, 0, (this.#cards.length - 1) * this.#step);
    }
    this.#layout();
  };

  #syncMedia(activeIndex) {
    if (!this.#isNearViewport) return;
    if (activeIndex === this.#activeIndex) return;
    this.#activeIndex = activeIndex;

    this.#cards.forEach((card, index) => {
      if (index !== activeIndex) this.#pauseCardMedia(card);
    });

    const activeCard = this.#cards[activeIndex];
    if (activeCard) this.#playCardMedia(activeCard);
  }

  #scheduleMediaSync(activeIndex, delay) {
    clearTimeout(this.#pendingMediaTimer);

    if (activeIndex === this.#activeIndex) return;

    if (delay === 0) {
      this.#syncMedia(activeIndex);
      return;
    }

    this.#pendingMediaTimer = setTimeout(() => {
      this.#pendingMediaTimer = undefined;
      this.#syncMedia(activeIndex);
    }, delay);
  }

  #suspendMedia() {
    clearTimeout(this.#pendingMediaTimer);
    this.#pendingMediaTimer = undefined;

    if (this.#activeIndex === -1) return;

    this.#cards.forEach((card) => this.#pauseCardMedia(card));
    this.#activeIndex = -1;
  }

  #playActiveCardMediaFromGesture() {
    if (!this.#isNearViewport || !this.#cards.length) return;

    const position = Math.round(this.#rotation / this.#step);
    const activeIndex = this.#hasFiniteBounds()
      ? clamp(position, 0, this.#cards.length - 1)
      : modulo(position, this.#cards.length);
    const activeCard = this.#cards[activeIndex];
    if (activeCard) this.#playCardMedia(activeCard);
  }

  #playCardMedia(card) {
    if (window.tncIsSlowDevice?.()) {
      this.#pauseCardMedia(card);
      return;
    }

    const deferredMediaElements = card.querySelectorAll('deferred-media');

    deferredMediaElements.forEach((media) => {
      if (!media.querySelector('video, iframe') && media.querySelector('template')) {
        media.loadContent?.(false);
      }

      media.playMedia?.();
    });

    card.querySelectorAll('video').forEach((video) => {
      if (video.dataset.ringPlaybackListener !== 'true') {
        video.dataset.ringPlaybackListener = 'true';
        video.addEventListener('loadeddata', () => {
          card.classList.add('card-media-has-frame');
        }, { signal: this.#abortController?.signal });
        video.addEventListener('playing', () => {
          card.classList.add('card-media-has-frame');
          if (card.classList.contains('card-active')) card.classList.add('card-media-playing');
        }, { signal: this.#abortController?.signal });
      }
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) card.classList.add('card-media-has-frame');
      if (!video.paused && card.classList.contains('card-active')) card.classList.add('card-media-playing');
      this.#configureVideo(video);
      const playPromise = video.play();
      playPromise?.catch((error) => {
        if (error?.name !== 'NotAllowedError') return;
        video.dataset.ringAutoplayBlocked = 'true';
        video.autoplay = false;
        video.removeAttribute('autoplay');
      });
    });

    card.querySelectorAll('iframe').forEach((iframe) => this.#controlIframe(iframe, true));
  }

  #pauseCardMedia(card) {
    card.classList.remove('card-media-playing');
    card.querySelectorAll('deferred-media').forEach((media) => media.pauseMedia?.());

    card.querySelectorAll('video').forEach((video) => {
      this.#configureVideo(video);
      video.pause();
    });

    card.querySelectorAll('iframe').forEach((iframe) => this.#controlIframe(iframe, false));
  }

  #controlIframe(iframe, shouldPlay) {
    iframe.dataset.ringPlayback = shouldPlay ? 'play' : 'pause';

    if (!this.#controlledIframes.has(iframe)) {
      this.#controlledIframes.add(iframe);
      iframe.addEventListener(
        'load',
        () => this.#sendIframeCommand(iframe, iframe.dataset.ringPlayback === 'play'),
        { signal: this.#abortController?.signal }
      );
    }

    this.#sendIframeCommand(iframe, shouldPlay);
  }

  #sendIframeCommand(iframe, shouldPlay) {
    const isYouTube = iframe.classList.contains('js-youtube') || iframe.dataset.videoType === 'youtube';
    const isVimeo = iframe.classList.contains('js-vimeo') || iframe.dataset.videoType === 'vimeo';

    if (isYouTube) {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: shouldPlay ? 'playVideo' : 'pauseVideo', args: '' }),
        '*'
      );
    } else if (isVimeo) {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: shouldPlay ? 'play' : 'pause' }), '*');
    }
  }

  #configureVideo(video) {
    video.controls = false;
    video.removeAttribute('controls');
    video.disablePictureInPicture = true;
    video.setAttribute('disablepictureinpicture', '');
    video.defaultMuted = true;
    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    // Playback is managed explicitly by the active-card controller. Keeping
    // this attribute absent prevents iOS from injecting native pause/play UI.
    video.autoplay = false;
    video.removeAttribute('autoplay');
  }

  #scheduleStickyMetrics = () => {
    cancelAnimationFrame(this.#stickyMetricsFrame);
    this.#stickyMetricsFrame = requestAnimationFrame(this.#refreshStickyMetrics);
  };

  #bindLenisScroll = () => {
    this.#lenisScrollUnsubscribe?.();
    this.#lenisVirtualScrollUnsubscribe?.();
    this.#lenisScrollUnsubscribe = window.tncLenis?.on?.('scroll', this.#onWindowScroll);
    this.#lenisVirtualScrollUnsubscribe = window.tncLenis?.on?.('virtual-scroll', this.#onLenisVirtualScroll);
    this.#scheduleStickyMetrics();
  };

  #getScrollPosition() {
    const lenisScroll = Number(window.tncLenis?.scroll);
    if (Number.isFinite(lenisScroll)) return lenisScroll;

    const scrollContainer = this.#getScrollContainer();
    return scrollContainer instanceof Window ? window.scrollY : scrollContainer.scrollTop;
  }

  #getScrollContainer() {
    const lenisWrapper = window.tncLenis?.options?.wrapper;
    if (lenisWrapper instanceof HTMLElement) return lenisWrapper;
    if (window.matchMedia('(min-width: 990px)').matches && this.#pageWrapper instanceof HTMLElement) {
      return this.#pageWrapper;
    }
    return window;
  }

  #refreshStickyMetrics = () => {
    this.#stickyMetricsFrame = undefined;

    const section = this.#stickySection;
    const target = this.#stickyTarget;
    const canStick =
      this.hasAttribute('mobile-sticky-scroll') &&
      !this.#usesSlowNonStickyMode() &&
      section instanceof HTMLElement &&
      target instanceof HTMLElement &&
      this.#cards.length > 1;

    this.#stickyActive = Boolean(canStick);

    if (!canStick || !(section instanceof HTMLElement) || !(target instanceof HTMLElement)) {
      this.#lastScrollPosition = undefined;
      this.#touchPageSyncPending = false;
      clearTimeout(this.#touchPageSyncTimer);
      this.#finishScrollGesture();
      section?.style.removeProperty('--carousel-mobile-sticky-extra');
      section?.removeAttribute('carousel-sticky-measuring');
      target?.removeAttribute('carousel-touch-capture');
      window.tncLenis?.resize?.();
      this.removeAttribute('scroll-driven');
      this.#layout();
      return;
    }

    const scrollPerCard = toNumber(this.getAttribute('mobile-scroll-per-card'), 280);
    const stickyTop = toNumber(this.getAttribute('mobile-sticky-top'), 48);
    const extraDistance = scrollPerCard * (this.#cards.length - 1);

    section.style.setProperty('--carousel-mobile-sticky-extra', `${extraDistance}px`);
    window.tncLenis?.resize?.();

    section.setAttribute('carousel-sticky-measuring', '');
    const scrollPosition = this.#getScrollPosition();
    const scrollContainer = this.#getScrollContainer();
    const scrollViewportTop = scrollContainer instanceof Window ? 0 : scrollContainer.getBoundingClientRect().top;
    const sectionTop = section.getBoundingClientRect().top - scrollViewportTop + scrollPosition;
    const targetTop = target.getBoundingClientRect().top - scrollViewportTop + scrollPosition;
    section.removeAttribute('carousel-sticky-measuring');

    this.#stickyStart = targetTop - stickyTop;
    this.#stickyEnd = Math.max(
      this.#stickyStart + extraDistance,
      sectionTop + section.offsetHeight - stickyTop - target.offsetHeight
    );

    this.#lastScrollPosition = scrollPosition;
    this.#layout();
    this.#stabilizeStickyPosition();
  };

  #onWindowScroll = () => {
    if (!this.#stickyActive) return;
    this.#updateTouchCaptureState();
    if (this.#stickyScrollFrame) return;

    this.#stickyScrollFrame = requestAnimationFrame(() => {
      this.#stickyScrollFrame = undefined;
      this.#stabilizeStickyPosition();
    });
  };

  #stabilizeStickyPosition() {
    this.#updateTouchCaptureState();

    if (
      !this.#stickyActive ||
      this.#scrollSyncInProgress ||
      this.#touchPageSyncPending ||
      this.hasAttribute('dragging')
    ) {
      return;
    }

    const scrollPosition = this.#getScrollPosition();
    const previousScrollPosition = this.#lastScrollPosition;
    this.#lastScrollPosition = scrollPosition;

    const enteredFromAbove =
      previousScrollPosition < this.#stickyStart &&
      scrollPosition >= this.#stickyStart &&
      scrollPosition <= this.#stickyEnd;
    const enteredFromBelow =
      previousScrollPosition > this.#stickyEnd &&
      scrollPosition <= this.#stickyEnd &&
      scrollPosition >= this.#stickyStart;

    if (enteredFromAbove || enteredFromBelow) {
      this.#rotation = (enteredFromBelow ? this.#cards.length - 1 : 0) * this.#step;
      this.#layout();
      this.#syncWindowToRotation();
      return;
    }

    const isWithinStickyRange =
      scrollPosition >= this.#stickyStart - STICKY_POSITION_TOLERANCE &&
      scrollPosition <= this.#stickyEnd + STICKY_POSITION_TOLERANCE;
    if (!isWithinStickyRange) return;

    const maximumRotation = (this.#cards.length - 1) * this.#step;
    const progress = maximumRotation > 0 ? clamp(this.#rotation / maximumRotation, 0, 1) : 0;
    const selectedCardScroll = this.#stickyStart + (this.#stickyEnd - this.#stickyStart) * progress;

    // A sticky carousel owns page position while it has more cards in the
    // current direction. Cancel residual page momentum instead of allowing it
    // to overwrite a card chosen by buttons, dragging, or a prior wheel step.
    if (Math.abs(scrollPosition - selectedCardScroll) > STICKY_POSITION_TOLERANCE) {
      this.#syncWindowToRotation();
    }
  }

  #canSelectRelative(direction) {
    const position = clamp(Math.round(this.#rotation / this.#step), 0, this.#cards.length - 1);
    return direction > 0 ? position < this.#cards.length - 1 : position > 0;
  }

  #isAtStickyCapturePosition() {
    if (!this.#stickyActive || this.#stickyEnd <= this.#stickyStart) return false;

    const scrollPosition = this.#getScrollPosition();
    return (
      scrollPosition >= this.#stickyStart - STICKY_POSITION_TOLERANCE &&
      scrollPosition <= this.#stickyEnd + STICKY_POSITION_TOLERANCE
    );
  }

  #updateTouchCaptureState() {
    this.#stickyTarget?.toggleAttribute('carousel-touch-capture', this.#isAtStickyCapturePosition());
  }

  #finishScrollGesture = () => {
    clearTimeout(this.#scrollGestureTimer);
    this.#scrollGestureActive = false;
    this.#desktopScrollDelta = 0;
  };

  #captureScrollDelta(
    delta,
    { finishOnIdle = true, deferTouchPageSync = false, allowContinuous = false } = {}
  ) {
    if (!delta || !this.#isAtStickyCapturePosition()) return false;

    const direction = delta > 0 ? 1 : -1;
    if (!this.#canSelectRelative(direction)) {
      // Do not let the one-card-per-gesture guard consume outward momentum at
      // either end. Release any in-flight page sync so this input can continue
      // into the surrounding page immediately.
      this.#finishScrollGesture();
      this.#releasePageScrollAtBoundary(direction);
      return false;
    }

    if (this.#scrollGestureActive) {
      if (allowContinuous && window.matchMedia('(min-width: 750px)').matches) {
        const scrollPerCard = Math.max(1, toNumber(this.getAttribute('mobile-scroll-per-card'), 400));
        this.#desktopScrollDelta += delta;

        while (Math.abs(this.#desktopScrollDelta) >= scrollPerCard) {
          const continuedDirection = this.#desktopScrollDelta > 0 ? 1 : -1;
          if (!this.#canSelectRelative(continuedDirection)) {
            this.#desktopScrollDelta = 0;
            break;
          }

          this.#desktopScrollDelta -= continuedDirection * scrollPerCard;
          this.#selectRelative(continuedDirection);
        }
      }

      if (finishOnIdle) {
        clearTimeout(this.#scrollGestureTimer);
        this.#scrollGestureTimer = setTimeout(this.#finishScrollGesture, SCROLL_GESTURE_IDLE_DURATION);
      }
      return true;
    }

    this.#scrollGestureActive = true;
    if (deferTouchPageSync) this.#holdPageForTouch();
    this.#selectRelative(direction, { syncPage: !deferTouchPageSync });

    if (finishOnIdle) {
      this.#scrollGestureTimer = setTimeout(this.#finishScrollGesture, SCROLL_GESTURE_IDLE_DURATION);
    }
    return true;
  }

  #holdPageForTouch() {
    this.#touchPageSyncPending = true;
    ++this.#scrollSyncId;
    this.#scrollSyncInProgress = false;
    clearTimeout(this.#scrollSyncTimer);

    const scrollPosition = this.#getScrollPosition();
    if (window.tncLenis) {
      window.tncLenis.reset?.();
      return;
    }

    const scrollContainer = this.#getScrollContainer();
    scrollContainer.scrollTo({
      top: scrollPosition,
      left: scrollContainer instanceof Window ? window.scrollX : scrollContainer.scrollLeft,
      behavior: 'auto',
    });
  }

  #scheduleTouchPageSync() {
    if (!this.#touchPageSyncPending) return;

    clearTimeout(this.#touchPageSyncTimer);
    // Run in the task after touchend/touchcancel has fully propagated. Starting
    // the page animation inside that event lets large swipe momentum
    // fight the programmatic scroll and makes the sticky frame oscillate.
    this.#touchPageSyncTimer = setTimeout(() => {
      this.#touchPageSyncTimer = undefined;
      this.#touchPageSyncPending = false;
      this.#syncWindowToRotation();
    }, 0);
  }

  #scheduleTouchPageScroll(delta) {
    if (!this.#touchPageSyncPending) return;

    clearTimeout(this.#touchPageSyncTimer);
    this.#touchPageSyncTimer = setTimeout(() => {
      this.#touchPageSyncTimer = undefined;
      this.#touchPageSyncPending = false;
      if (!delta) {
        this.#updateTouchCaptureState();
        return;
      }

      const activePosition = clamp(Math.round(this.#rotation / this.#step), 0, this.#cards.length - 1);
      const boundaryScroll = activePosition === this.#cards.length - 1 ? this.#stickyEnd : this.#stickyStart;
      const targetScroll = boundaryScroll + delta;
      if (window.tncLenis?.scrollTo) {
        window.tncLenis.reset?.();
        window.tncLenis.scrollTo(targetScroll, { force: true });
      } else {
        const scrollContainer = this.#getScrollContainer();
        scrollContainer.scrollTo({
          top: targetScroll,
          left: scrollContainer instanceof Window ? window.scrollX : scrollContainer.scrollLeft,
          behavior: 'smooth',
        });
      }
    }, 0);
  }

  #getPrimaryScrollDelta(deltaX, deltaY) {
    return Math.abs(deltaY) >= Math.abs(deltaX) ? deltaY : deltaX;
  }

  #getWheelScrollDelta(event) {
    const delta = this.#getPrimaryScrollDelta(event.deltaX, event.deltaY);
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return delta * 16;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return delta * window.innerHeight;
    return delta;
  }

  #releasePageScrollAtBoundary(direction) {
    const boundaryScroll = direction > 0 ? this.#stickyEnd : this.#stickyStart;
    const lenisNeedsRelease = window.tncLenis?.isLocked || this.#scrollSyncInProgress;
    if (
      !lenisNeedsRelease &&
      Math.abs(this.#getScrollPosition() - boundaryScroll) <= STICKY_POSITION_TOLERANCE
    ) {
      return;
    }

    // If the user continues outward before the card-to-page animation has
    // settled, finish at the boundary immediately and let this same input
    // continue into the surrounding page.
    ++this.#scrollSyncId;
    this.#scrollSyncInProgress = false;
    clearTimeout(this.#scrollSyncTimer);

    if (window.tncLenis?.scrollTo) {
      window.tncLenis.reset?.();
      window.tncLenis.scrollTo(boundaryScroll, { force: true, immediate: true });
    } else {
      const scrollContainer = this.#getScrollContainer();
      scrollContainer.scrollTo({
        top: boundaryScroll,
        left: scrollContainer instanceof Window ? window.scrollX : scrollContainer.scrollLeft,
        behavior: 'auto',
      });
    }

    this.#lastScrollPosition = boundaryScroll;
  }

  #onWheel = (event) => {
    if (!(event instanceof WheelEvent) || event.carouselRingScrollHandled) return;

    const delta = this.#getWheelScrollDelta(event);
    if (!this.#captureScrollDelta(delta, { allowContinuous: true })) return;

    event.carouselRingScrollHandled = true;
    event.lenisStopPropagation = true;
    event.preventDefault();
  };

  #onTouchStart = (event) => {
    const touch = event.touches?.[0];
    const startedInsideStickyFrame =
      event.target instanceof Node && this.#stickyTarget?.contains(event.target);
    this.#touchStartX = touch?.clientX;
    this.#touchStartY = touch?.clientY;
    this.#touchLastY = touch?.clientY;
    this.#touchAxis = undefined;
    this.#touchGestureCaptured = Boolean(startedInsideStickyFrame && this.#isAtStickyCapturePosition());
    this.#touchGestureAction = undefined;
    this.#touchOutwardDelta = 0;
    this.#finishScrollGesture();

    if (this.#touchGestureCaptured) {
      event.carouselRingScrollHandled = true;
      event.lenisStopPropagation = true;
    }
  };

  #onTouchMove = (event) => {
    const touch = event.touches?.[0];
    if (!touch || !this.#touchGestureCaptured || this.#touchLastY === undefined) return;

    event.carouselRingScrollHandled = true;
    event.lenisStopPropagation = true;
    event.preventDefault();

    const totalX = (this.#touchStartX ?? touch.clientX) - touch.clientX;
    const totalY = (this.#touchStartY ?? touch.clientY) - touch.clientY;
    const deltaY = this.#touchLastY - touch.clientY;
    this.#touchLastY = touch.clientY;

    if (!this.#touchAxis) {
      if (Math.max(Math.abs(totalX), Math.abs(totalY)) < 8) return;
      this.#touchAxis = Math.abs(totalY) >= Math.abs(totalX) ? 'vertical' : 'horizontal';
    }
    if (this.#touchAxis !== 'vertical' || !deltaY) return;

    if (this.#touchGestureAction === 'page') {
      this.#touchOutwardDelta += deltaY;
      return;
    }

    if (this.#touchGestureAction === 'card') {
      this.#captureScrollDelta(deltaY, { finishOnIdle: false, deferTouchPageSync: true });
      return;
    }

    const direction = deltaY > 0 ? 1 : -1;
    if (this.#canSelectRelative(direction)) {
      this.#touchGestureAction = 'card';
      this.#captureScrollDelta(deltaY, { finishOnIdle: false, deferTouchPageSync: true });
    } else {
      this.#touchGestureAction = 'page';
      this.#touchOutwardDelta = deltaY;
      this.#holdPageForTouch();
    }
  };

  #onTouchEnd = (event) => {
    const touchGestureAction = this.#touchGestureAction;
    const touchOutwardDelta = this.#touchOutwardDelta;

    if (this.#touchGestureCaptured || this.#scrollGestureActive || this.#touchPageSyncPending) {
      event.carouselRingScrollHandled = true;
      event.lenisStopPropagation = true;
    }
    this.#touchStartX = undefined;
    this.#touchStartY = undefined;
    this.#touchLastY = undefined;
    this.#touchAxis = undefined;
    this.#touchGestureCaptured = false;
    this.#touchGestureAction = undefined;
    this.#touchOutwardDelta = 0;
    this.#finishScrollGesture();
    // Direct playback from touchend is the permitted fallback when iOS
    // rejects ordinary muted autoplay, including in Low Power Mode.
    this.#playActiveCardMediaFromGesture();

    if (touchGestureAction === 'page') this.#scheduleTouchPageScroll(touchOutwardDelta);
    else if (touchGestureAction === 'card') this.#scheduleTouchPageSync();
  };

  #onLenisVirtualScroll = ({ deltaX, deltaY, event }) => {
    if (!event || event.carouselRingScrollHandled) return;

    const isTouch = event.type?.includes('touch');
    if (isTouch) return;

    // Touch gestures are owned by the document-level handlers above. This
    // listener handles Lenis wheel/trackpad input only.
    const delta = this.#getPrimaryScrollDelta(deltaX, deltaY);
    if (!this.#captureScrollDelta(delta, { allowContinuous: true })) return;

    event.carouselRingScrollHandled = true;
    event.lenisStopPropagation = true;
    event.preventDefault?.();
  };

  #syncWindowToRotation() {
    if (!this.#stickyActive || this.#stickyEnd <= this.#stickyStart) return;

    const maximumRotation = (this.#cards.length - 1) * this.#step;
    const progress = maximumRotation > 0 ? clamp(this.#rotation / maximumRotation, 0, 1) : 0;
    const targetScroll = this.#stickyStart + (this.#stickyEnd - this.#stickyStart) * progress;
    const syncId = ++this.#scrollSyncId;
    this.#scrollSyncInProgress = true;
    clearTimeout(this.#scrollSyncTimer);

    const finishScrollSync = () => {
      if (syncId !== this.#scrollSyncId) return;
      this.#scrollSyncInProgress = false;
      clearTimeout(this.#scrollSyncTimer);
      this.#lastScrollPosition = this.#getScrollPosition();
      this.#stabilizeStickyPosition();
    };

    this.#scrollSyncTimer = setTimeout(finishScrollSync, 1800);

    if (window.tncLenis?.scrollTo) {
      // Reset discards the wrapper's previous vertical target, which otherwise
      // can reassert itself after a horizontal carousel interaction completes.
      window.tncLenis.reset?.();
      window.tncLenis.scrollTo(targetScroll, { force: true, lock: true, onComplete: finishScrollSync });
    } else {
      const scrollContainer = this.#getScrollContainer();
      scrollContainer.addEventListener('scrollend', finishScrollSync, { once: true });
      scrollContainer.scrollTo({
        top: targetScroll,
        left: scrollContainer instanceof Window ? window.scrollX : scrollContainer.scrollLeft,
        behavior: 'smooth',
      });
    }
  }

  #onPointerUp = (event) => {
    if (!(event instanceof PointerEvent) || event.pointerId !== this.#dragPointerId) return;
    this.#dragPointerId = undefined;
    if (!this.hasAttribute('dragging')) return;

    const scene = this.refs.scene;
    if (scene instanceof HTMLElement && scene.hasPointerCapture(event.pointerId)) {
      scene.releasePointerCapture(event.pointerId);
    }
    this.removeAttribute('dragging');
    this.#rotation = Math.round(this.#rotation / this.#step) * this.#step;
    if (this.#hasFiniteBounds()) {
      this.#rotation = clamp(this.#rotation, 0, (this.#cards.length - 1) * this.#step);
    }
    this.#suppressClick = this.#dragMoved;
    this.#layout();
    if (this.#dragMoved) this.#syncWindowToRotation();

    if (this.#suppressClick) setTimeout(() => (this.#suppressClick = false), 0);
  };

  #onPointerCancel = (event) => {
    if (!(event instanceof PointerEvent) || event.pointerId !== this.#dragPointerId) return;
    this.#dragPointerId = undefined;
    if (!this.hasAttribute('dragging')) return;
    this.removeAttribute('dragging');
    this.#rotation = Math.round(this.#rotation / this.#step) * this.#step;
    if (this.#hasFiniteBounds()) {
      this.#rotation = clamp(this.#rotation, 0, (this.#cards.length - 1) * this.#step);
    }
    this.#layout();
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.#suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const card = event.target.closest('[data-carousel-card]');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;
    if (event.target.closest('a[href]')) return;

    const index = Number.parseInt(card.dataset.ringIndex ?? '', 10);
    const activeIndex = modulo(Math.round(this.#rotation / this.#step), this.#cards.length);
    if (!Number.isFinite(index) || index === activeIndex) return;

    event.preventDefault();
    event.stopPropagation();
    this.select(index);
  };

  #onKeyDown = (event) => {
    if (!(event instanceof KeyboardEvent)) return;

    if (event.key === 'ArrowRight') this.next(event);
    else if (event.key === 'ArrowLeft') this.previous(event);
    else if (event.key === 'Home') {
      event.preventDefault();
      this.select(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.select(this.#cards.length - 1);
    }
  };

  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const card = event.target.closest('[data-carousel-card]');
    if (!(card instanceof HTMLElement)) return;

    const index = this.#cards.indexOf(card);
    if (index >= 0) this.select(index);
  };
}

if (!customElements.get('carousel-ring-component')) {
  customElements.define('carousel-ring-component', CarouselRing);
}
